import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDown,
    faChevronUp,
    faCheckCircle,
    faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import styles from './StudentAnalytics.module.css';

export interface QuizAttempt {
    id: number;
    attemptNumber: number;
    score: number;
    date: string;
    time: string;
    questions: {
        id: string;
        question: string;
        yourSelection: string;
        correctAnswer: string;
        status: string;
    }[];
}

interface QuizAttemptsOverviewProps {
    attempts: QuizAttempt[];
}

const QuizAttemptsOverview: React.FC<QuizAttemptsOverviewProps> = ({ attempts }) => {
    const [expandedId, setExpandedId] = useState<number | null>(null);

    const toggleExpand = (id: number) => {
        setExpandedId(expandedId === id ? null : id);
    };

    if (!attempts || attempts.length === 0) return null;

    return (
        <div className={styles['attempts-container']}>
            {attempts.map((attempt) => (
                <div key={attempt.id} className={`${styles['attempt-card']} ${expandedId === attempt.id ? styles.expanded : ''}`}>
                    <div className={styles['attempt-header']} onClick={() => toggleExpand(attempt.id)}>
                        <div className={styles['header-item']}>
                            <span className={styles['item-label']}>ATTEMPT</span>
                            <span className={styles['item-value']}>Attempt #{attempt.attemptNumber}</span>
                        </div>
                        <div className={styles['header-divider']} />
                        <div className={styles['header-item']}>
                            <span className={styles['item-label']}>SCORE</span>
                            <span className={`${styles['item-value']} ${attempt.score >= 80 ? styles['text-green'] : attempt.score >= 60 ? styles['text-yellow'] : styles['text-red']}`}>
                                {attempt.score.toFixed(1)}%
                            </span>
                        </div>
                        <div className={styles['header-divider']} />
                        <div className={styles['header-item']}>
                            <span className={styles['item-label']}>DATE & TIME</span>
                            <span className={styles['item-value']}>{attempt.date} • {attempt.time}</span>
                        </div>
                        <div className={styles['header-action']}>
                            <span>View Details</span>
                            <FontAwesomeIcon icon={expandedId === attempt.id ? faChevronUp : faChevronDown} />
                        </div>
                    </div>
                    {expandedId === attempt.id && (
                        <div className={styles['attempt-details']}>
                            <table className={styles['details-table']}>
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>QUESTION</th>
                                        <th>YOUR SELECTION</th>
                                        <th>CORRECT ANSWER</th>
                                        <th style={{ textAlign: 'right' }}>STATUS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {attempt.questions.map((q) => (
                                        <tr key={q.id}>
                                            <td className={styles['question-id']}>{q.id}</td>
                                            <td className={styles['question-text']}>{q.question}</td>
                                            <td className={`${styles['user-selection']} ${q.status === 'INCORRECT' ? styles['text-red-bold'] : ''}`}>{q.yourSelection}</td>
                                            <td className={styles['correct-answer']}>{q.correctAnswer}</td>
                                            <td style={{ textAlign: 'right' }}>
                                                <span className={`${styles['status-pill']} ${q.status === 'CORRECT' ? styles['pill-green'] : styles['pill-red']}`}>
                                                    <FontAwesomeIcon icon={q.status === 'CORRECT' ? faCheckCircle : faExclamationCircle} style={{ marginRight: '6px' }} />
                                                    {q.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default QuizAttemptsOverview;
